import { ArrowUpRight, Ban, Inbox, RotateCcw, Send, Truck } from 'lucide-react';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { usePermission } from '../../../../auth/AuthContext';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { CountBadge } from '../../../../components/CountBadge';
import { ErrorState } from '../../../../components/ErrorState';
import { AuditTrailList } from '../../../../components/index-page/AuditTrailList';
import { PreviewTable } from '../../../../components/PreviewTable';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { Skeleton } from '../../../../components/Skeleton';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime } from '../../../../lib/formatDate';
import { personsApi } from '../../../human-resources/personnel/personnelApi';
import { DispatchedDocumentModal } from '../components/DispatchedDocumentModal';
import { RoutingHistoryPanel } from '../components/RoutingHistoryPanel';
import { RoutingPreviewPanel } from '../components/RoutingPreviewPanel';
import { RoutingStatusPill } from '../components/RoutingStatusPill';
import { dispatchedDocumentsApi } from '../routingApi';
import { DISPATCHING_PATH, RECEIVING_PATH } from '../routingRules';
import { ROUTING_STATUS, type DispatchedDocumentDetail } from '../routingTypes';

type TabKey = 'info' | 'preview' | 'routing' | 'audit';

/**
 * One dispatched document — what went out, to whom, its files, where it had been before it left,
 * where it has been since a unit's Inbox took it in, and every write ever made to it.
 *
 * **Its Routing tab is its SOURCE's whole route** where it sends on a received document — the
 * server returns those lines with the dispatch line last — so where an outgoing letter has been
 * is one table. A document a unit wrote has its own line, and — sent to a unit — every line its
 * Inbox has added since. **This page moves nothing**: the sender records and corrects the
 * dispatch; the unit it reached moves it on, from its Inbox.
 *
 * Tabs as Receiving's: **Preview** on `download`, **Audit trail** on `audit-logs.read`, landing on
 * Info. The banner states where it went and links back to the document it sent, under Receiving's
 * own read key — hidden without it, not implied.
 */
export function DispatchedDocumentProfilePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const canReadPeople = usePermission('personnel.read');

  const [document, setDocument] = useState<DispatchedDocumentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [tab, setTab] = useState<TabKey>('info');
  const [editing, setEditing] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [panelKey, setPanelKey] = useState(0);
  const [recipientName, setRecipientName] = useState<string | null>(null);

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      setLoading(true);

      dispatchedDocumentsApi
        .get(id, signal)
        .then((next) => {
          if (signal?.aborted) return;

          setDocument(next);
          setError(null);

          if (next.recipientPersonId == null || !canReadPeople) {
            setRecipientName(null);
            return;
          }

          personsApi
            .resolve([next.recipientPersonId], signal)
            .then((people) => {
              if (!signal?.aborted) setRecipientName(people[0]?.displayName ?? null);
            })
            .catch(() => setRecipientName(null));
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setError(caught instanceof ApiError ? caught : new ApiError('Could not load this dispatch.', 0));
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [id, canReadPeople],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  const changed = useCallback(() => {
    setPanelKey((k) => k + 1);
    load();
  }, [load]);

  /** An undo — no tick and no password. The server refuses one over a route that carried on. */
  async function restore() {
    if (!document) return;

    setRestoring(true);

    try {
      await dispatchedDocumentsApi.restore(document.id);
      toast.success(`Restored ${document.trackingNumber}.`);
      changed();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('Could not restore this dispatch.', 0));
    } finally {
      setRestoring(false);
    }
  }

  const breadcrumb: BreadcrumbSegment[] = [
    'Document Management',
    'Routing',
    { label: 'Dispatching', to: DISPATCHING_PATH },
    document?.trackingNumber ?? 'Dispatch',
  ];

  if (error && !document) {
    return (
      <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState message={error.message} onRetry={() => load()} />
      </div>
    );
  }

  if (loading && !document) {
    return (
      <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-4 p-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-64 w-full max-w-4xl" />
        </div>
      </div>
    );
  }

  if (!document) return null;

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell<TabKey>
        id="dispatched-document-profile"
        panel={tab === 'info' ? 'cards' : 'bleed'}
        header={
          <ProfileHeader
            id="dispatched-document-profile-header"
            icon={<Truck className="h-6 w-6" aria-hidden="true" />}
            title={document.subject}
            meta={[
              document.trackingNumber,
              document.documentTypeName,
              document.mediumLabel,
              `To ${document.recipientOrgUnitName ?? document.recipientOffice ?? '—'}`,
            ]}
            status={<RoutingStatusPill status={document.status} label={document.statusLabel} isActive={document.isActive} />}
            createdBy={document.createdByName}
            createdAt={document.createdAt}
          />
        }
        banner={
          error ? (
            <ProfileBanner
              id="dispatched-document-profile-banner"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message={error.message}
            />
          ) : !document.isActive ? (
            <ProfileBanner
              id="dispatched-document-profile-banner"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message={
                document.receivedDocumentId != null
                  ? `This dispatch is deleted, and ${document.receivedTrackingNumber} is back on hand where it was. Its files are kept.`
                  : 'This dispatch is deleted. Its files are kept.'
              }
              action={
                <PermissionGate permission="document-dispatching.delete">
                  <Button
                    id="dispatched-document-profile-restore"
                    variant="secondary"
                    leading={<RotateCcw className="h-4 w-4" aria-hidden="true" />}
                    onClick={() => void restore()}
                    disabled={restoring}
                    loading={restoring}
                    loadingLabel="Restoring"
                  >
                    Restore
                  </Button>
                </PermissionGate>
              }
            />
          ) : (
            <ProfileBanner
              id="dispatched-document-profile-banner"
              icon={stateOf(document).icon}
              message={stateOf(document).message}
              action={
                document.receivedDocumentId != null ? (
                  <PermissionGate permission="document-receiving.read">
                    <Button
                      id="dispatched-document-profile-open-source"
                      variant="secondary"
                      leading={<ArrowUpRight className="h-4 w-4" aria-hidden="true" />}
                      onClick={() => navigate(`${RECEIVING_PATH}/${document.receivedDocumentId}`)}
                    >
                      Open {document.receivedTrackingNumber}
                    </Button>
                  </PermissionGate>
                ) : null
              }
            />
          )
        }
        tabs={[
          { key: 'info', label: 'Info' },
          {
            key: 'preview',
            permission: 'document-dispatching.download',
            label: (
              <span className="inline-flex items-center gap-1.5">
                Preview <CountBadge count={document.attachmentCount} />
              </span>
            ),
          },
          {
            key: 'routing',
            label: (
              <span className="inline-flex items-center gap-1.5">
                Routing <CountBadge count={document.routeCount} />
              </span>
            ),
          },
          { key: 'audit', label: 'Audit trail', permission: 'audit-logs.read' },
        ]}
        activeTab={tab}
        onTabChange={setTab}
      >
        {tab === 'info' ? (
          <>
            <ProfileCard
              id="dispatched-document-profile-details"
              title="Details"
              onEdit={() => setEditing(true)}
              editPermission="document-dispatching.update"
            >
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Tracking no.', value: document.trackingNumber, mono: true },
                  { label: 'Subject', value: document.subject },
                  { label: 'Document type', value: document.documentTypeName },
                  { label: 'Sent as', value: document.mediumLabel },
                  {
                    label: 'Sends on',
                    value:
                      document.receivedDocumentId != null
                        ? [document.receivedTrackingNumber, document.receivedSubject].filter(Boolean).join(' — ')
                        : 'A document the unit wrote',
                  },
                  { label: 'From unit', value: document.originatingOrgUnitName },
                  // Both rows, always — one is a dash, which says which side of the organization it went to.
                  { label: 'To (unit)', value: document.recipientOrgUnitName },
                  { label: 'To (organization)', value: document.recipientOffice },
                  {
                    // `Hidden` is NOT an em dash — the dispatch HAS a named addressee. A unit never has one.
                    label: 'Named addressee',
                    value: document.recipientPersonId == null ? null : canReadPeople ? recipientName : 'Hidden',
                  },
                  { label: 'Sent by', value: document.modeLabel },
                  { label: 'Dispatched', value: formatDateTime(document.dispatchedAt), mono: true },
                  { label: 'Waybill / receipt no.', value: document.carrierReferenceNumber, mono: true },
                  {
                    label: 'Received by the unit',
                    value: document.inboxReceivedAt != null ? formatDateTime(document.inboxReceivedAt) : null,
                    mono: true,
                  },
                  { label: 'Received by', value: document.inboxReceivedByName },
                  // A SET — several units once it has been routed on to more than one; one line, joined.
                  { label: 'With', value: document.holders.length > 0 ? document.holders.map((h) => h.name).join(', ') : null },
                  { label: 'Remarks', value: document.remarks },
                ]}
              />
            </ProfileCard>

            <ProfileCard id="dispatched-document-profile-audit" title="Audit">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Created by', value: document.createdByName },
                  { label: 'Created', value: formatDateTime(document.createdAt), mono: true },
                  { label: 'Updated by', value: document.updatedByName },
                  { label: 'Updated', value: document.updatedAt ? formatDateTime(document.updatedAt) : null, mono: true },
                ]}
              />
            </ProfileCard>
          </>
        ) : tab === 'preview' ? (
          <RoutingPreviewPanel
            key={panelKey}
            recordId={document.id}
            trackingNumber={document.trackingNumber}
            api={dispatchedDocumentsApi}
            modulePrefix="document-dispatching"
            isActive={document.isActive}
            onChanged={() => load()}
          />
        ) : tab === 'routing' ? (
          <div className="flex h-full min-h-0 flex-col p-4">
            <RoutingHistoryPanel key={panelKey} recordId={document.id} api={dispatchedDocumentsApi} onChanged={changed} />
          </div>
        ) : (
          <div className="flex h-full min-h-0 flex-col p-4">
            <AuditTrailList
              module="document-dispatching"
              entityId={document.id}
              emptyMessage="No recorded activity for this dispatch yet."
            />
          </div>
        )}
      </ProfileHeaderShell>

      {editing ? (
        <DispatchedDocumentModal
          open
          editing={document}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            changed();
          }}
        />
      ) : null}
    </div>
  );
}

/** What the banner says while the dispatch is live — one sentence per state, every state. */
function stateOf(document: DispatchedDocumentDetail): { icon: ReactNode; message: string } {
  const sent = `from ${document.originatingOrgUnitName} on ${formatDateTime(document.dispatchedAt)}, ${document.modeLabel.toLowerCase()}`;
  const closes = document.receivedDocumentId != null ? ` It closes the route of ${document.receivedTrackingNumber}.` : '';

  // An outside office — the route ends there; nobody acknowledges it here.
  if (document.recipientOrgUnitId == null) {
    return {
      icon: <Truck className="h-4 w-4" aria-hidden="true" />,
      message: `Sent to ${document.recipientOffice ?? 'an outside office'} ${sent}.${closes}`,
    };
  }

  if (document.inboxReceivedAt == null) {
    return {
      icon: <Send className="h-4 w-4" aria-hidden="true" />,
      message: `In transit — sent to ${document.recipientOrgUnitName} ${sent}. It is ${document.originatingOrgUnitName}'s until ${document.recipientOrgUnitName}'s Inbox receives it.${closes}`,
    };
  }

  return {
    icon: <Inbox className="h-4 w-4" aria-hidden="true" />,
    message: `Received by ${document.recipientOrgUnitName} on ${formatDateTime(document.inboxReceivedAt)}${
      document.inboxReceivedByName ? ` by ${document.inboxReceivedByName}` : ''
    }. ${
      document.status === ROUTING_STATUS.inTransit
        ? `It is with ${with_(document)}, and on its way to another unit.`
        : `It is with ${with_(document)}.`
    }${closes}`,
  };
}

/** Every unit it is with, joined — a set since 10 Oct 2026. */
function with_(document: DispatchedDocumentDetail): string {
  return document.holders.map((h) => h.name).join(', ') || 'no unit';
}
